import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App/App";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import { store } from "./Store";
import '@fontsource/open-sans/300.css';
import '@fontsource/open-sans/400.css';
import '@fontsource/open-sans/500.css';
import '@fontsource/open-sans/600.css';
import '@fontsource/open-sans/700.css';
import '@fontsource/open-sans/800.css';
import '@fontsource/open-sans/300-italic.css';
import '@fontsource/open-sans/400-italic.css';
import '@fontsource/open-sans/500-italic.css';
import '@fontsource/open-sans/600-italic.css';
import '@fontsource/open-sans/700-italic.css';
import '@fontsource/open-sans/800-italic.css';
import '@fortawesome/fontawesome-pro/css/all.min.css';
import '@fortawesome/fontawesome-pro/css/duotone-light.min.css';
import '@fortawesome/fontawesome-pro/css/duotone-thin.min.css';
import '@fortawesome/fontawesome-pro/css/duotone-regular.min.css';
import '@fortawesome/fontawesome-pro/css/sharp-duotone-solid.min.css';
import './Css/Variables.css';
import './Css/common-imports.css';
import { MsalProvider } from "@azure/msal-react";
import { msalInstance } from "./Config/msalConfig";
import { AlertProvider } from './Components/Common/Firestore/Alerts/AlertContext';
import { AccountEditNotificationProvider } from './context/AccountEditNotifications';
import { ClientMessageProvider } from './Components/Common/Firestore/Client/ClientMessageContext';
import { GeneralMessageProvider } from './Components/Common/Firestore/General/GeneralMessageContext';
import { WorkspaceClientProvider } from './context/WorkspaceClientContext';
import { initTelemetry } from './Services/TelemetryService';
import { ErrorBoundary } from './Components/Common/ErrorBoundary';
import { reloadOnceForChunkError, isPreloadErrorSuppressed } from './utils/chunkReload';
import { DeploymentUpdateToast } from './Components/Common/DeploymentUpdateToast';
import { JobChangesProvider } from './context/JobChanges';

// Initialize telemetry before rendering
initTelemetry();

// Vite emits this when a code-split chunk fails to load — almost always because a
// new version was deployed and the old hashed chunk no longer exists. Reload once
// to pick up the fresh build instead of letting the error surface as a white screen.
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  // Don't reload for failed background/idle preloads — only for real navigations.
  if (isPreloadErrorSuppressed()) return;
  reloadOnceForChunkError();
});

// Unregister any stale service workers (e.g. MSW's mockServiceWorker.js) that may
// have been registered in a previous session. A lingering worker intercepts fetch
// requests and can hang indefinitely in Firefox when the client-side MSW library
// is not running.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <Suspense>
        <Provider store={store}>
          <WorkspaceClientProvider>
          <MsalProvider instance={msalInstance}>
            <AlertProvider>
              <JobChangesProvider>
              <AccountEditNotificationProvider>
              <ClientMessageProvider>
                <GeneralMessageProvider>
                  <App/>
                  <DeploymentUpdateToast />
                </GeneralMessageProvider>
              </ClientMessageProvider>
              </AccountEditNotificationProvider>
              </JobChangesProvider>
            </AlertProvider>
          </MsalProvider>
          </WorkspaceClientProvider>
        </Provider>
      </Suspense>
    </ErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to a monitoring endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
